import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, MapPin, PlugZap, Wifi, WifiOff, Plus, Loader2, ServerCrash, RefreshCw, Zap } from "lucide-react";
import { motion } from "framer-motion";
import "./StationList.scss";
import SearchBox from "../../lib/components/SearchBox";
import CreateStationForm from "./CreateStationForm";
import useHierarchy from "../../lib/hooks/useHierarchy";

export default function StationList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const { stations, isLoading: loading, isError, refresh: refreshStations } = useHierarchy();
  const error = isError ? "Failed to load stations" : null;

  const showCreateForm = searchParams.get("mode") === "create";

  /* Filter out "Unassigned" — it is not a real station */
  const realStations = useMemo(() => {
    return stations.filter(s => {
      const name = (s.stationName || s.name || "").toLowerCase();
      return name !== "unassigned";
    });
  }, [stations]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return realStations;
    const q = searchQuery.toLowerCase();
    return realStations.filter(
      s =>
        (s.stationName || s.name || "").toLowerCase().includes(q) ||
        String(s.stationId || s.id || "")
          .toLowerCase()
          .includes(q) ||
        (s.address || s.location || "").toLowerCase().includes(q)
    );
  }, [realStations, searchQuery]);

  if (showCreateForm) {
    return (
      <div className="station-list">
        <CreateStationForm
          onBack={() => navigate("/stations/manage")}
          onCreated={id => navigate(`/stations/manage/${id}`)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="station-list__state">
        <motion.div
          className="station-list__state-icon station-list__state-icon--loading"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 size={36} />
        </motion.div>
        <h3 className="station-list__state-title">Loading Stations...</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div className="station-list__state">
        <div className="station-list__state-icon station-list__state-icon--error">
          <ServerCrash size={36} />
        </div>
        <h3 className="station-list__state-title">Something went wrong</h3>
        <p className="station-list__state-desc">{error}</p>
        <button className="station-list__state-btn" onClick={refreshStations}>
          <RefreshCw size={16} /> Retry
        </button>
      </div>
    );
  }

  if (realStations.length === 0) {
    return (
      <div className="station-list__state">
        <div className="station-list__state-icon station-list__state-icon--empty">
          <Building2 size={40} />
        </div>
        <h3 className="station-list__state-title">No Stations Found</h3>
        <p className="station-list__state-desc">
          Get started by creating your first Charging Station. You can add multiple sites to create a Station.
        </p>
        <button
          className="station-list__state-btn station-list__state-btn--primary"
          onClick={() => navigate("/stations/manage?mode=create")}
        >
          <Plus size={16} /> Create Station
        </button>
      </div>
    );
  }

  return (
    <div className="station-list">
      {/* Toolbar */}
      <div className="station-list__toolbar">
        <SearchBox
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery("")}
          placeholder="Search stations by name, ID, or location..."
          width="50%"
        />
      </div>

      {/* Table */}
      <div className="station-list__table-wrap">
        <table className="station-list__table">
          <thead>
            <tr>
              <th>Station</th>
              <th>Location</th>
              <th>Sites</th>
              <th>Chargers</th>
              <th>Online</th>
              <th>Max Breaker</th>
              <th>Remaining</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((station, i) => {
              const name = station.stationName || station.name || "Unnamed";
              const id = station.stationId || station.id;
              const location = station.address || station.location || "";
              const sites = station.sites || [];
              const siteCount = sites.length || station.siteIds?.length || 0;
              let chargerCount = 0;
              let onlineCount = 0;
              let totalRatedCurrent = 0;
              sites.forEach(site => {
                const chargers = site.chargers || [];
                chargerCount += chargers.length;
                chargers.forEach(c => {
                  if (c.isConnected) onlineCount += 1;
                  totalRatedCurrent += c.ratedCurrent || c.maxCurrent || 0;
                });
              });
              const maxBreaker = station.maxBreakerCurrent;
              const remaining = maxBreaker != null ? maxBreaker - totalRatedCurrent : null;

              return (
                <motion.tr
                  key={id || i}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="station-list__row"
                  onClick={() => navigate(`/stations/manage/${id}`)}
                  style={{ cursor: "pointer" }}
                  role="link"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === "Enter") navigate(`/stations/manage/${id}`);
                  }}
                >
                  <td>
                    <div className="station-list__name-cell">
                      <Building2 size={16} className="station-list__name-icon" />
                      <div>
                        <div className="station-list__station-name">{name}</div>
                        <div className="station-list__station-id">{id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="station-list__location-cell">
                      <MapPin size={13} />
                      {location || "—"}
                    </div>
                  </td>
                  <td>
                    <span className="station-list__badge">{siteCount}</span>
                  </td>
                  <td>
                    <div className="station-list__charger-cell">
                      <PlugZap size={13} />
                      {chargerCount}
                    </div>
                  </td>
                  <td>
                    <div className="station-list__online-cell">
                      <Wifi size={13} className="station-list__online-icon" />
                      {onlineCount}
                      {onlineCount < chargerCount && (
                        <span className="station-list__offline-count">
                          <WifiOff size={11} />
                          {chargerCount - onlineCount}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {maxBreaker != null ? (
                      <div className="station-list__breaker-cell">
                        <Zap size={13} />
                        {maxBreaker}A
                      </div>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </td>
                  <td>
                    {remaining != null ? (
                      <span
                        className="station-list__badge"
                        style={{
                          color: remaining >= 0 ? "#059669" : "#dc2626",
                          fontWeight: 600,
                        }}
                      >
                        {remaining}A
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="station-list__empty">
            <Building2 size={36} />
            <p>No stations found</p>
          </div>
        )}
      </div>
    </div>
  );
}
